import type { ReactNode } from "react";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Lock,
  RotateCw,
  Search,
  ShoppingCart,
  Star,
  X,
} from "lucide-react";
import { Hotspot, useAccent } from "@/ui";
import { hotspotById } from "./hotspots";
import type { HotspotId } from "./hotspots";

interface ProductPageMockProps {
  selected: HotspotId | null;
  showAll: boolean;
  visited: ReadonlySet<HotspotId>;
  onSelect: (id: HotspotId) => void;
}

/* ── highlightable region: accent ring when selected, dims when another is ── */

interface RegionProps {
  id: HotspotId;
  selected: HotspotId | null;
  pin: ReactNode;
  className?: string;
  /** Rounding etc. for the highlighted content wrapper (ring follows it). */
  innerClassName?: string;
  children: ReactNode;
}

function Region({
  id,
  selected,
  pin,
  className = "",
  innerClassName = "rounded-lg",
  children,
}: RegionProps) {
  const [from] = useAccent();
  const isSelected = selected === id;
  const dimmed = selected !== null && !isSelected;
  return (
    <div className={`relative ${className}`}>
      <div
        className={`transition-all duration-300 ${innerClassName}`}
        style={{
          opacity: dimmed ? 0.3 : 1,
          filter: dimmed ? "saturate(0.5)" : "none",
          boxShadow: isSelected
            ? `0 0 0 3px ${from}, 0 0 28px ${from}88`
            : "none",
        }}
      >
        {children}
      </div>
      {pin}
    </div>
  );
}

/* ── CSS/SVG-drawn running shoe, side profile — no external images ── */

function ShoeArt() {
  return (
    <svg
      viewBox="0 0 220 120"
      className="mx-auto h-36 w-full max-w-[17rem]"
      aria-hidden
    >
      {/* upper */}
      <path
        d="M18 90 C14 52 26 34 46 32 C64 30 76 46 102 58 C130 71 168 78 200 87 L202 90 Z"
        fill="#404040"
      />
      {/* heel collar highlight */}
      <path d="M24 62 C26 46 34 39 46 37 C40 46 35 54 33 66 Z" fill="#595959" />
      {/* accent stripe along the midfoot */}
      <path
        d="M42 90 C72 64 112 72 152 83 L196 89 L196 90 L42 90 Z"
        fill="#34d399"
      />
      {/* laces */}
      {[0, 1, 2, 3].map((i) => (
        <line
          key={i}
          x1={72 + i * 14}
          y1={48 + i * 5}
          x2={86 + i * 14}
          y2={41 + i * 5}
          stroke="#f5f5f5"
          strokeWidth={3.5}
          strokeLinecap="round"
        />
      ))}
      {/* midsole */}
      <path
        d="M14 90 L204 90 Q213 90 211 99 L209 101 L12 101 Q8 95 14 90 Z"
        fill="#e5e5e5"
      />
      {/* outsole */}
      <path
        d="M12 101 L209 101 Q212 107 198 110 L26 110 Q10 109 12 101 Z"
        fill="#171717"
      />
      {/* arch-support wedge in the midsole */}
      <path d="M52 90 L108 90 L104 101 L60 101 Z" fill="#a7f3d0" />
    </svg>
  );
}

/* ── mini "related guide" card ── */

function GuideCard({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-white p-3">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-50">
        <BookOpen className="h-6 w-6 text-emerald-600" />
      </div>
      <p className="min-w-0 truncate text-xl font-medium leading-snug text-blue-700 underline decoration-blue-300 underline-offset-4">
        {title}
      </p>
    </div>
  );
}

/* ── the browser window + product page ── */

export function ProductPageMock({
  selected,
  showAll,
  visited,
  onSelect,
}: ProductPageMockProps) {
  const pin = (id: HotspotId, className: string) => {
    const info = hotspotById(id);
    return (
      <Hotspot
        n={info.n}
        selected={selected === id || showAll}
        visited={visited.has(id)}
        onClick={() => onSelect(id)}
        className={`absolute ${className}`}
        label={info.title}
      />
    );
  };

  return (
    <div className="flex h-full w-full flex-col rounded-2xl border border-white/10 bg-ink-800/90 shadow-[0_24px_80px_rgba(0,0,0,0.55)] backdrop-blur-xl">
      {/* ── tab strip ── */}
      <div className="flex shrink-0 items-center gap-4 px-5 pt-3">
        <div className="flex items-center gap-2" aria-hidden>
          <span className="h-3.5 w-3.5 rounded-full bg-accent-rose/80" />
          <span className="h-3.5 w-3.5 rounded-full bg-accent-amber/80" />
          <span className="h-3.5 w-3.5 rounded-full bg-accent-emerald/80" />
        </div>

        {/* 1 · title tag (the browser tab) */}
        <Region
          id="title-tag"
          selected={selected}
          innerClassName="rounded-t-xl"
          pin={pin("title-tag", "-right-4 -top-2")}
        >
          <div className="flex items-center gap-3 rounded-t-xl border-x border-t border-white/10 bg-white/10 px-5 py-2.5">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-emerald-600 font-display text-base font-bold text-white">
              S
            </span>
            <span className="max-w-md truncate text-xl text-white/85">
              Stability Runner — Flat Feet | Stride
            </span>
            <X className="h-4 w-4 shrink-0 text-white/40" />
          </div>
        </Region>

        {/* 2 · meta description (SERP preview chip near the tab) */}
        <Region
          id="meta-description"
          selected={selected}
          innerClassName="rounded-full"
          pin={pin("meta-description", "-right-5 -top-3")}
        >
          <div className="flex items-center gap-2.5 rounded-full border border-dashed border-white/25 bg-white/5 px-5 py-2">
            <Search className="h-5 w-5 text-white/50" />
            <span className="whitespace-nowrap text-xl text-white/60">
              SERP preview
            </span>
          </div>
        </Region>
      </div>

      {/* ── address bar ── */}
      <div className="flex shrink-0 items-center gap-3 border-b border-white/10 bg-white/[0.03] px-5 py-2.5">
        <ArrowLeft className="h-5 w-5 shrink-0 text-white/35" aria-hidden />
        <ArrowRight className="h-5 w-5 shrink-0 text-white/20" aria-hidden />
        <RotateCw className="h-5 w-5 shrink-0 text-white/35" aria-hidden />

        {/* 3 · descriptive URL */}
        <Region
          id="url"
          selected={selected}
          className="min-w-0 flex-1"
          innerClassName="rounded-full"
          pin={pin("url", "right-3 -top-3")}
        >
          <div className="flex items-center gap-2.5 rounded-full bg-white/10 px-5 py-2">
            <Lock className="h-4 w-4 shrink-0 text-accent-emerald" />
            <p className="truncate font-mono text-xl">
              <span className="text-white/50">stride.example</span>
              <span className="text-white/90">
                /running/stability-shoes/stability-runner-flat-feet
              </span>
            </p>
          </div>
        </Region>
      </div>

      {/* ── the page (light storefront surface) ── */}
      <div className="relative min-h-0 flex-1 rounded-b-2xl bg-[#f8f8f8] px-10 pb-7 pt-4 font-sans text-neutral-900">
        {/* breadcrumbs — page furniture, not a pin */}
        <nav className="w-fit rounded-lg px-1 py-0.5 text-xl text-neutral-500">
          Home <span className="mx-1 text-neutral-400">›</span> Running
          <span className="mx-1 text-neutral-400">›</span>{" "}
          <span className="font-medium text-neutral-800">Stability Shoes</span>
        </nav>

        <div className="mt-3 grid grid-cols-[minmax(0,20rem)_minmax(0,1fr)] gap-10">
          {/* 7 · image alt + lazy-load (product image) */}
          <Region
            id="alt-lazy"
            selected={selected}
            innerClassName="rounded-xl"
            pin={pin("alt-lazy", "-left-3 -top-3")}
          >
            <div className="rounded-xl border border-neutral-200 bg-white px-5 pb-3 pt-5">
              <ShoeArt />
              <p className="mt-2 truncate text-center font-mono text-xl text-neutral-400">
                stability-runner-side.jpg
              </p>
            </div>
          </Region>

          <div className="flex min-w-0 flex-col gap-3 pr-16">
            {/* 4 · one clear H1 */}
            <Region
              id="h1"
              selected={selected}
              className="w-fit max-w-full"
              pin={pin("h1", "-right-14 top-1")}
            >
              <h1 className="rounded-lg px-1 font-display text-4xl font-bold tracking-tight text-neutral-900">
                Stability Runner — built for flat feet
              </h1>
            </Region>

            {/* 9 · structured data (price + rating block) */}
            <Region
              id="schema"
              selected={selected}
              className="w-fit"
              pin={pin("schema", "-right-14 top-1/2 -translate-y-1/2")}
            >
              <div className="flex items-center gap-5 rounded-lg px-1 py-0.5">
                <span className="flex items-center gap-1" aria-label="Rated 4.6 stars">
                  {[0, 1, 2, 3].map((i) => (
                    <Star key={i} className="h-6 w-6 fill-amber-400 text-amber-400" />
                  ))}
                  <Star className="h-6 w-6 fill-neutral-300 text-neutral-300" />
                  <span className="ml-1.5 text-xl font-semibold text-neutral-700">
                    4.6
                  </span>
                </span>
                <span className="h-7 w-px bg-neutral-300" aria-hidden />
                <span className="font-display text-4xl font-bold text-neutral-900">
                  $129
                </span>
                <span className="rounded-full bg-emerald-100 px-3 py-0.5 text-xl font-medium text-emerald-700">
                  In stock
                </span>
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-full bg-emerald-600 px-6 py-2 text-xl font-semibold text-white transition hover:bg-emerald-700"
                >
                  <ShoppingCart className="h-5 w-5" />
                  Add to cart
                </button>
              </div>
            </Region>

            {/* 6 · body copy with naturally bolded phrases */}
            <Region
              id="copy"
              selected={selected}
              pin={pin("copy", "-right-14 top-1/2 -translate-y-1/2")}
            >
              <p className="rounded-lg px-1 text-xl leading-relaxed text-neutral-600">
                Built for{" "}
                <strong className="font-semibold text-neutral-900">flat feet</strong>,
                the Stability Runner pairs firm{" "}
                <strong className="font-semibold text-neutral-900">
                  arch support
                </strong>{" "}
                with a soft, cushioned ride — steady on long runs, light enough for{" "}
                <strong className="font-semibold text-neutral-900">
                  weeknight training
                </strong>
                .
              </p>
            </Region>

            {/* 5 · H2–H3 structure (section subheads) */}
            <Region
              id="headings"
              selected={selected}
              innerClassName="rounded-xl"
              pin={pin("headings", "-right-14 top-1/2 -translate-y-1/2")}
            >
              <div className="grid grid-cols-2 gap-6 rounded-xl px-1 py-1">
                <div>
                  <h2 className="flex items-center gap-2 text-2xl font-semibold text-neutral-900">
                    <span className="rounded bg-neutral-200 px-1.5 font-mono text-xl font-medium text-neutral-500">
                      H2
                    </span>
                    Why stability matters
                  </h2>
                  <p className="mt-1 text-xl leading-snug text-neutral-500">
                    Flat arches tend to roll inward as you tire — a stability shoe
                    guides each stride back to neutral.
                  </p>
                </div>
                <div>
                  <h3 className="flex items-center gap-2 text-xl font-semibold text-neutral-900">
                    <span className="rounded bg-neutral-200 px-1.5 font-mono text-xl font-medium text-neutral-500">
                      H3
                    </span>
                    Fit &amp; sizing
                  </h3>
                  <p className="mt-1 text-xl leading-snug text-neutral-500">
                    True to size with a roomy toe box — go half a size up for thick
                    socks.
                  </p>
                </div>
              </div>
            </Region>
          </div>
        </div>

        {/* 8 · internal links ("Related guides" row + inline guide link) */}
        <Region
          id="internal-links"
          selected={selected}
          className="mt-4"
          innerClassName="rounded-xl"
          pin={pin("internal-links", "-right-3 -top-3")}
        >
          <div className="rounded-xl px-1 py-1">
            <div className="flex items-baseline justify-between gap-6 px-1">
              <h2 className="text-2xl font-semibold text-neutral-900">
                Related guides
              </h2>
              <span className="cursor-pointer whitespace-nowrap text-xl font-medium text-blue-700 underline underline-offset-4">
                Read our running shoe fitting guide →
              </span>
            </div>
            <div className="mt-2.5 grid grid-cols-3 gap-4">
              <GuideCard title="Flat feet: choosing support" />
              <GuideCard title="Stability vs neutral shoes" />
              <GuideCard title="When to replace your shoes" />
            </div>
          </div>
        </Region>

        {/* 10 · fast core page (floating vitals badge) */}
        <Region
          id="vitals"
          selected={selected}
          className="absolute bottom-4 right-7 z-10"
          innerClassName="rounded-full"
          pin={pin("vitals", "-left-4 -top-4")}
        >
          <div className="flex items-center gap-2.5 rounded-full border border-emerald-200 bg-white px-5 py-2 shadow-lg">
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" aria-hidden />
            <span className="whitespace-nowrap font-mono text-xl font-medium text-emerald-600">
              LCP 1.8s · INP 120ms · CLS 0.03
            </span>
          </div>
        </Region>
      </div>
    </div>
  );
}
